/* eslint-disable react/no-danger */
import dynamic from 'next/dynamic';
import { SagaStore, wrapper } from 'store';
import { NextSeo } from 'next-seo';
import { useRouter } from 'next/router';
import {
  LIST_FAVICON_DEFAULT,
  LIST_LINK_STYLE_SHEET,
} from '@containers/Home/constants';
import { I_Portfolio } from '@containers/Home/typesIndex';
import { GET_PORTFOLIO } from '@containers/Home/slice';
import { END } from 'redux-saga';
import Script from 'next/script';

const Home = dynamic(() => import('containers/Home'), {
  loading: () => <span>Loading</span>,
});

interface Props {
  slug: string;
  host?: string;
  user?: I_Portfolio['user'];
}

function PortfolioContainer({ slug, host, user }: Props) {
  const router = useRouter();

  return (
    <>
      <NextSeo
        title={user?.name || user?.email || 'Panoee Portfolio'}
        description={user?.bio || 'Panoee - Virtual Tour 360'}
        additionalLinkTags={[...LIST_FAVICON_DEFAULT, ...LIST_LINK_STYLE_SHEET]}
        canonical={`${process.env.NEXT_PUBLIC_BASE_URL}/${slug}`}
        openGraph={{
          type: 'website',
          url: `${process.env.NEXT_PUBLIC_BASE_URL}/${slug}`,
          title: user?.name || user?.email || 'Panoee Portfolio',
          description: user?.bio || 'Panoee - Virtual Tour 360',
          images: [
            {
              url: user?.avatar || user?.logo || user?.cover,
              width: 800,
              height: 600,
              alt: user?.bio || 'Panoee - Virtual Tour 360',
            },
          ],
          site_name: user?.name || user?.email || 'Panoee Portfolio',
          profile: {
            username: user?.name as string,
          },
        }}
        twitter={{
          site: '@panoee360',
          cardType: 'summary',
        }}
        additionalMetaTags={[]}
      />

      <Home />

      <Script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org/',
            '@type': 'Person',
            name: user?.name || user?.email || 'Panoee Portfolio',
            url: host
              ? `https://${host}/${router.asPath}`
              : process.env.NEXT_PUBLIC_BASE_URL + router.asPath,
            image: user?.avatar || user?.logo || user?.cover,
            sameAs: user?.socials?.map(item => item.value) || [],
          }),
        }}
      />
    </>
  );
}

export const getServerSideProps = wrapper.getServerSideProps(
  store =>
    async ({ query, req }) => {
      if (query.slug) {
        store.dispatch(
          GET_PORTFOLIO({
            slug: query.slug,
            menuCollectionSelected: query.collection || null,
          }),
        );
        store.dispatch(END);
        await (store as SagaStore).sagaTask?.toPromise();
      }
      return {
        props: {
          slug: query.slug || '',
          host: req.headers.host,
          user: store.getState().app?.portfolio?.user || null,
        },
      };
    },
);

export default PortfolioContainer;
